import { z } from "zod";
import { digest } from "../../canonical";
import { BuilderDatabase } from "../../postgres";
import { postPhotoRefs } from "./markdown";
/**
 * Bounds only: a post's cover isn't part of its revision, so its pixel size isn't known here. The
 * 1200:630 aspect check and the rounding clamp run against the real image when the share image is made (`shareCrop.ts`).
 */
export const CropSchema = z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1), width: z.number().min(0.01).max(1), height: z.number().min(0.01).max(1) }).strict()
    .refine((crop) => crop.x + crop.width <= 1 + 1e-6 && crop.y + crop.height <= 1 + 1e-6, "Image crop must fit the image");
export const PostSeoSchema = z.object({
    title: z.string().trim().min(1).max(160).optional(),
    description: z.string().trim().min(1).max(500).optional(),
    imageCrop: CropSchema.optional(),
}).strict();
export const PostRevisionInputSchema = z.object({ postId: z.string().regex(/^[A-Za-z][A-Za-z0-9_-]{0,63}$/).optional(), bodyMd: z.string().max(200_000), seo: PostSeoSchema.default({}) }).strict();
/** A stable count for the writing view. Serving does not depend on its exact value. */
export function postWordCount(bodyMd) {
    return bodyMd.replace(/\{[^}]*\}/g, " ").replace(/[:*#_>`[\]()-]/g, " ").match(/[\p{L}\p{N}]+(?:['’][\p{L}\p{N}]+)*/gu)?.length ?? 0;
}
export async function postRevisionDigest(revision) {
    return digest({ bodyMd: revision.bodyMd, seo: revision.seo, wordCount: revision.wordCount });
}
/** Small edits and metadata changes do not turn model prose into an owner voice sample. */
export function substantiallyEdited(original, edited) {
    const words = (text) => text.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
    const before = words(original), after = words(edited);
    if (JSON.stringify(before) === JSON.stringify(after))
        return false;
    const pairs = (list) => list.slice(1).map((word, i) => `${list[i]} ${word}`);
    const known = new Set(pairs(before));
    const retained = pairs(after).filter(pair => known.has(pair)).length;
    return retained / Math.max(before.length - 1, after.length - 1, 1) < 0.65;
}
export class PostRevisionStore {
    db;
    constructor(db) {
        this.db = db;
    }
    async save(siteId, actor, input, source) {
        const parsed = PostRevisionInputSchema.parse(input);
        const revision = { bodyMd: parsed.bodyMd, seo: parsed.seo, wordCount: postWordCount(parsed.bodyMd) };
        const hash = await postRevisionDigest(revision);
        const refs = postPhotoRefs(parsed.bodyMd);
        const photos = await this.db.withSite(siteId, actor, async (client) => {
            const generated = source === "stax-draft" ? [] : (await client.query(`SELECT body_md FROM builder_post_revisions WHERE site_id=$1 AND stax_drafted AND (
          digest=(SELECT working->>'revision' FROM builder_posts WHERE site_id=$1 AND id=$2)
          OR digest IN (SELECT digest FROM builder_post_revisions WHERE site_id=$1 AND stax_drafted ORDER BY created_at DESC LIMIT 30))`, [siteId, parsed.postId ?? null])).rows;
            const staxDrafted = source === "stax-draft" || generated.some(row => !substantiallyEdited(row.body_md, revision.bodyMd));
            await client.query(`INSERT INTO builder_post_revisions(site_id,digest,body_md,seo,word_count,stax_drafted) VALUES($1,$2,$3,$4::jsonb,$5,$6 OR EXISTS(SELECT 1 FROM builder_post_revisions WHERE site_id=$1 AND body_md=$3 AND stax_drafted))
        ON CONFLICT (site_id,digest) DO NOTHING`, [siteId, hash, revision.bodyMd, JSON.stringify(revision.seo), revision.wordCount, staxDrafted]);
            // The photos this body uses, recorded once so serving can authorize them without reading any body.
            // A photo the site doesn't have is skipped, and renders nothing.
            if (refs.length)
                await client.query(`INSERT INTO builder_post_revision_assets(site_id,digest,asset_id,version)
        SELECT a.site_id,$2,a.asset_id,a.version FROM builder_post_assets a WHERE a.site_id=$1 AND (a.asset_id,a.version) IN (SELECT * FROM unnest($3::text[],$4::int[]))
        ON CONFLICT DO NOTHING`, [siteId, hash, refs.map((ref) => ref.assetId), refs.map((ref) => ref.version)]);
            return this.photos(siteId, hash, client);
        });
        return { digest: hash, revision: { ...revision, photos } };
    }
    async load(siteId, hash, connection = this.db.pool) {
        if (!/^[a-f0-9]{64}$/.test(hash))
            return null;
        const rows = await connection.query("SELECT body_md,seo,word_count,created_at FROM builder_post_revisions WHERE site_id=$1 AND digest=$2", [siteId, hash]);
        const row = rows.rows[0];
        if (!row)
            return null;
        const seo = PostSeoSchema.parse(typeof row.seo === "string" ? JSON.parse(row.seo) : row.seo);
        const revision = { bodyMd: row.body_md, seo, wordCount: row.word_count };
        if (await postRevisionDigest(revision) !== hash)
            throw new Error("Post revision integrity check failed");
        return { ...revision, photos: await this.photos(siteId, hash, connection), savedAt: new Date(row.created_at).toISOString() };
    }
    async photos(siteId, hash, connection) {
        const rows = await connection.query(`SELECT a.asset_id,a.version,a.mime_type,a.width,a.height FROM builder_post_revision_assets r JOIN builder_post_assets a
        ON a.site_id=r.site_id AND a.asset_id=r.asset_id AND a.version=r.version WHERE r.site_id=$1 AND r.digest=$2 ORDER BY a.asset_id,a.version`, [siteId, hash]);
        return rows.rows.map((row) => ({ assetId: row.asset_id, version: row.version, mimeType: row.mime_type, ...(row.width && row.height ? { width: row.width, height: row.height } : {}) }));
    }
}
